import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const GlobalPartnershipsSection = () => {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const partnerships = [
    {
      name: "SPATZ3",
      location: "New York, USA",
      description: "Obesity management solutions",
      category: "Obesity Management"
    },
    {
      name: "CONMED",
      location: "New York, USA", 
      description: "Endoscopic technologies",
      category: "Endoscopy"
    },
    {
      name: "AMA-MED",
      location: "Mikkeli, Finland",
      description: "Rapid urease tests",
      category: "Diagnostics"
    },
    {
      name: "SHAILI ENDOSCOPY",
      location: "Gujarat, India",
      description: "Endoscopic instruments",
      category: "Endoscopy"
    },
    {
      name: "MICONVEY",
      location: "Shanghai, China",
      description: "Medical imaging systems",
      category: "Imaging"
    },
    {
      name: "M.I.TECH",
      location: "Gyeonggi-do, Korea",
      description: "Surgical instruments",
      category: "Surgery"
    },
    {
      name: "MEDNOVA",
      location: "Zhejiang, China",
      description: "Medical devices",
      category: "Medical Devices"
    },
    {
      name: "FUSHAN MEDICAL",
      location: "Zhejiang, China",
      description: "Healthcare solutions",
      category: "Healthcare"
    }
  ];

  const categories = [...new Set(partnerships.map(p => p.category))];

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-7xl mx-auto px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
            <Icon name="Globe" size={16} className="text-primary" />
            <span className="text-primary font-semibold text-sm">Global Network</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Global Partnerships
          </h2>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Strategic alliances with world-leading medical device manufacturers across 3 continents, 
            ensuring access to cutting-edge technology and innovative solutions.
          </p>
        </motion.div>

        {/* Map and Partnerships Display */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Map Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-medical border border-gray-200 dark:border-gray-600">
              {!imageError ? (
                <Image
                  src="/assets/images/partnership.jpg"
                  alt="Global Partnerships Map"
                  className="w-full h-auto rounded-xl"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              ) : (
                <div className="w-full h-64 bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl flex items-center justify-center">
                  <div className="text-center">
                    <Icon name="Globe" size={48} className="text-primary mx-auto mb-4" />
                    <p className="text-gray-700 dark:text-gray-300">Global Partnerships Map</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">8 Partners • 3 Continents</p>
                  </div>
                </div>
              )}
              {!imageLoaded && !imageError && (
                <div className="absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent rounded-2xl pointer-events-none flex items-center justify-center">
                  <Icon name="Loader" size={40} className="text-primary animate-spin" />
                </div>
              )}
              {imageError && (
                <div className="absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent rounded-2xl pointer-events-none flex items-center justify-center">
                  <Icon name="XCircle" size={40} className="text-red-500" />
                </div>
              )}
            </div>
            
            {/* Floating Stats */}
            <div className="absolute -top-4 -right-4 bg-primary text-white px-4 py-2 rounded-lg shadow-lg">
              <div className="text-sm font-medium">8 Partners</div>
              <div className="text-xs opacity-90">3 Continents</div>
            </div>
          </motion.div>

          {/* Partnerships List */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="grid grid-cols-2 gap-4 mb-8">
              {categories.map((category, index) => (
                <div key={category} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-primary rounded-full"></div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{category}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              {partnerships.slice(0, 6).map((partner, index) => (
                <motion.div
                  key={partner.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-medical transition-all duration-300 group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white group-hover:text-primary transition-colors duration-300">
                        {partner.name}
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{partner.location}</p>
                      <p className="text-xs text-primary font-medium">{partner.category}</p>
                    </div>
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                      <Icon name="ExternalLink" size={16} className="text-primary group-hover:text-white transition-colors duration-300" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Benefits of Global Partnerships */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-medical border border-gray-200 dark:border-gray-600"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Benefits of Our Global Network</h3>
            <p className="text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
              Our strategic partnerships provide Malaysian healthcare professionals with access to world-class technology and support.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Zap" size={24} className="text-primary" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Latest Technology</h4>
              <p className="text-sm text-gray-700 dark:text-gray-300">Access to cutting-edge medical devices and innovations</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-success/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Shield" size={24} className="text-success" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Quality Assurance</h4>
              <p className="text-sm text-gray-700 dark:text-gray-300">Products meet international standards and certifications</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Headphones" size={24} className="text-accent" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Global Support</h4>
              <p className="text-sm text-gray-700 dark:text-gray-300">Technical support and training from international experts</p>
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="bg-gradient-to-r from-primary/5 to-accent/5 dark:from-primary/10 dark:to-accent/10 rounded-2xl p-8 border border-primary/10 dark:border-primary/20">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Experience Global Medical Technology
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
              Partner with us to access world-leading medical devices and technology solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors duration-200"
                onClick={() => navigate('/product-portfolio')}
              >
                <Icon name="Package" size={16} className="inline mr-2" />
                Explore Products
              </button>
              <button 
                className="border border-primary text-primary dark:text-primary px-6 py-3 rounded-lg font-semibold hover:bg-primary/5 transition-colors duration-200"
                onClick={() => navigate('/contact')}
              >
                <Icon name="MessageCircle" size={16} className="inline mr-2" />
                Contact Partners
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GlobalPartnershipsSection;
